/* eslint-disable import/no-cycle */
import {Button, Col, Radio, Row} from 'antd';
import {CarryOutOutlined, UploadOutlined} from '@ant-design/icons';
import {observer, Observer} from 'mobx-react-lite';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {arrayMove} from '../../utils/helpers';
import {useMount} from '../../hooks/useMount';
import Table from '../shared/tables/Table';
import SortableTable from '../shared/tables/SortableTable';
import {Operation} from '../../models/operation';
import {ReportTypes} from '../../models/reportType';
import {ReportStates} from '../../models/reportLog';
import {useStateTransitionChanged} from '../../hooks/useStateTransitionChanged';
import {useStore} from '../../hooks/useStore';
import {getIcon, iconNames} from './orderWidgetConfigIcon';
import {useModulePolicy} from '../../hooks/useModulePolicy';
import ReorderButton from '../shared/buttons/ReorderButton';
import CancelButton from '../shared/buttons/CancelButton';
import SaveButton from '../shared/buttons/SaveButton';
import {sortChronologically} from '../shared/tables/sorters';
import {useOrderOperationsUpdated} from '../../hooks/useOrderOperationsUpdated';
import appConfig from '../../utils/appConfig';

const OrderTable = ({
  workplaceId,
  showTypeSelect = true,
  settings = {
    loadDaysPast: undefined,
    loadDaysFuture: undefined,
    sortOrderForFinishedOrders: 'desc',
  },
  columnsPlanned,
  columnsFinished,
  fromDate: overrideFromDate,
  toDate: overrideToDate,
  setDetailsModalVisible = () => undefined,
  // eslint-disable-next-line no-unused-vars
  useSelectedOperation = [undefined, (value) => undefined],
  // eslint-disable-next-line no-unused-vars
  useIsReordering = [undefined, (value) => false],
  isModalTable = false,
  setShowOperationDetails = () => undefined,
  setCurrentReportLog = () => undefined,
  setReportLogModalVisible = () => undefined,
  onTypeChange = () => undefined,
  ...props
}) => {
  const store = useStore();
  const [loadType, setLoadType] = useState(Operation.displayStates.planned);
  const {t} = useTranslation();
  const [fromDate, setFromDate] = useState(moment().subtract(settings.loadDaysPast, 'days').startOf('day'));
  const [toDate, setToDate] = useState(moment().add(settings.loadDaysFuture, 'days').startOf('day'));
  const [selectedOperation, setSelectedOperation] = useSelectedOperation;
  const stateTransitionChanged = useStateTransitionChanged(store);
  const orderOperationsUpdated = useOrderOperationsUpdated(store);
  const reorderPolicy = useModulePolicy(store, ['reorder-operations']);
  const [isReordering, setIsReordering] = useIsReordering;
  const [sortableOrders, setSortableOrders] = useState(undefined);

  const reportType = store.reportTypeStore.getByType(ReportTypes.PhasePerformance);

  const loadOperationsByDisplayState = (displayState) => {
    const loadActive = () => (store.operationStore.loadActiveOperationByWorkplace(workplaceId));

    const loadPlanned = () => store.operationStore.loadPlannedOperationsByWorkplace(workplaceId, {
      params: {
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
      },
    });

    const loadFinished = () => store.operationStore.loadFinishedOperationsByWorkplace(workplaceId, {
      params: {
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
      },
    }).then((operations) => {
      if (store.contextStore.isOfficeContext && reportType) {
        operations
          .forEach((operation) => {
            store.reportLogStore.loadAll({
              params: {
                operationId: operation.id,
                typeIds: [reportType.id],
              },
            });
          });
      }
    });

    if (workplaceId) {
      switch (displayState) {
        case Operation.displayStates.planned:
        case Operation.displayStates.active:
          loadActive();
          loadPlanned();
          break;
        case Operation.displayStates.finished:
          loadFinished();
          break;
        case Operation.displayStates.none:
          loadFinished();
          break;
        default:
          break;
      }
    }
  };

  useMount(() => {
    store.operationStateStore.loadAll();
    store.unitOfMeasurementStore.loadAll();
    store.reportTypeStore.loadAll();
    loadOperationsByDisplayState(Operation.displayStates.none);
    onTypeChange(loadType);
  });

  useMount(() => {
    if (overrideFromDate) {
      setFromDate(overrideFromDate);
    } else if (settings.loadDaysPast !== undefined) {
      setFromDate(moment().subtract(settings.loadDaysPast, 'day').startOf('day'));
    }
    if (overrideToDate) {
      setToDate(overrideToDate);
    } else if (settings.loadDaysFuture !== undefined) {
      setToDate(moment().add(settings.loadDaysFuture, 'day').startOf('day'));
    }
  }, [settings.loadDaysPast, settings.loadDaysFuture, overrideFromDate, overrideToDate]);

  useMount(() => {
    loadOperationsByDisplayState(loadType);
  }, [workplaceId, loadType, fromDate, toDate, stateTransitionChanged, orderOperationsUpdated]);

  const loading = [
    store.propertyStore,
    store.unitOfMeasurementStore,
    store.operationStore,
    store.materialStore,
    store.orderStore,
  ].some((s) => s.isLoadingCollection);

  const typeOptions = [
    {label: t('orderWidget.loadType.planned'), value: Operation.displayStates.planned},
    {label: t('orderWidget.loadType.finished'), value: Operation.displayStates.finished},
  ];

  const onClick = (record, isActionClick) => {
    if (!isReordering && record.order && !record.children) {
      if (isModalTable && record.disabled) {
        // do nothing
      } else if (isModalTable && isActionClick) {
        setShowOperationDetails(true);
      } else if (isModalTable && !isActionClick) {
        setSelectedOperation(record);
      } else if (store.contextStore.isOfficeContext && isActionClick) {
        setSelectedOperation(record);
        setDetailsModalVisible(true);
      } else if (store.contextStore.isOfficeContext && !isActionClick) {
        // do nothing
      } else {
        setSelectedOperation(record);
        setDetailsModalVisible(true);
      }
    }
  };

  const createReport = async (record) => {
    const oldReportLogs = store.reportLogStore.getByReportTypeIdAndOperationId(reportType.id, record.id);

    if (oldReportLogs && oldReportLogs.length > 0 && oldReportLogs[0].state === ReportStates.Draft) {
      await store.reportLogStore.delete(oldReportLogs[0].id, {skipNotification: true});
    }

    let newReportLog;
    if (!oldReportLogs || oldReportLogs.length === 0 || oldReportLogs[0].state === ReportStates.Draft) {
      newReportLog = await store.reportLogStore.create({
        operationId: record.id,
      }, {skipNotification: true});
    }

    if (newReportLog) {
      setCurrentReportLog(newReportLog);
      setReportLogModalVisible(true);
    } else if (oldReportLogs && oldReportLogs.length === 1) {
      setCurrentReportLog(oldReportLogs[0]);
      setReportLogModalVisible(true);
    }
  };

  const renderManualFinalPerformanceReportingButton = (record) => {
    if (loadType === Operation.displayStates.finished && !record.children) {
      const hasReport = store.reportLogStore.getByReportTypeIdAndOperationId(reportType.id, record.id)
        .filter((reportLog) => reportLog.state !== ReportStates.Draft).length > 0;
      return (
        <Button
          type={'link'}
          icon={(hasReport) ? <CarryOutOutlined/> : <UploadOutlined/>}
          onClick={() => createReport(record)}
          loading={store.reportLogStore.isLoadingCollection}
          disabled={record.order.state < 3}
        />
      );
    }
    return (
      <Button disabled icon={null}/>
    );
  };

  const rowClassNames = (record) => {
    const classNames = [];

    if (record.disabled && isModalTable) {
      classNames.push('disabled');
    } else if (store.contextStore.isOperatorContext) {
      classNames.push('pointable');
    }

    if (record.id === selectedOperation?.id && isModalTable) {
      classNames.push('selected');
    }

    return classNames.join(' ');
  };

  let tableColumns = loadType === Operation.displayStates.planned ? [...columnsPlanned] : [...columnsFinished];

  if (isModalTable || store.contextStore.isOfficeContext) {
    if (!isReordering) {
      tableColumns.push({
        key: 'actions',
        dataIndex: 'actions',
        render: (text, record) => ({
          props: {},
          children: (
            <>
              {store.contextStore.isOfficeContext && appConfig.modules.enableManualFinalPerformanceReporting && (
                renderManualFinalPerformanceReportingButton(record)
              )}
              {!record.children && (
                <Button type={'link'} onClick={() => onClick(record, true)} size={'small'}>
                  {t('orderWidget.table.action.showDetails')}
                </Button>
              )}
            </>
          ),
        }),
      });
    }
  }

  tableColumns = tableColumns.map((column) => ({
    title: column.label,
    ...column,
    render: (text, record, index) => (
      <Observer>
        {() => {
          const result = column.render(text, record, index);
          if (!result.props) {
            result.props = {};
          }
          if (!result.props.style) {
            result.props.style = {};
          }
          result.props.style.backgroundColor = settings && settings[`color.${record.displayStatus}`];

          if (settings && typeof result.children === 'string' && iconNames.includes(result.children)) {
            result.children = (
              <div style={{width: '100%', textAlign: 'center'}}>
                {getIcon(settings[`icon.${result.children}`], false)}
              </div>
            );
          }

          return (
            <div className={'order-table-data'} {...result.props}>
              {result.children}
            </div>
          );
        }}
      </Observer>
    ),
  }));

  const filteredData = () => {
    const filteredByDisplayState = loadType === Operation.displayStates.planned
      ? store.operationStore.aggregatedPlannedOperations
      : store.operationStore.aggregatedFinishedOperations
        .sort(
          (a, b) => {
            if (settings.sortOrderForFinishedOrders === 'desc') {
              return sortChronologically(b.actualStart, a.actualStart);
            }

            return sortChronologically(a.actualStart, b.actualStart);
          }
        );

    return filteredByDisplayState
      .filter((op) => {
        const fromOk = !fromDate || !op.plannedEnd || fromDate.isBefore(op.plannedEnd);
        const toOk = !toDate || toDate.isAfter(op.plannedStart);
        return fromOk && toOk;
      });
  };

  const calculateTmpSortOrder = (data) => {
    // set sortOrder as tmpSortOrder
    data.forEach((item) => {
      item.tmpSortOrder = item.sortOrder;
    });

    // when sortOrder of first record is the same or after the sortOrder of second record
    if (data.length > 1 && data[0].tmpSortOrder >= data[1].tmpSortOrder) {
      // set an new sortOrder of first record
      data[0].tmpSortOrder = data[1].tmpSortOrder - 0.5;
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 1; i < data.length; i++) {
      if (data[i].tmpSortOrder < data[i - 1].tmpSortOrder) {
        // if sortOrder of current record is before sortOrder of previous record
        // switch sortOrder of both records
        const currentItem = data[i].tmpSortOrder;
        const itemBefore = data[i - 1].tmpSortOrder;
        data[i].tmpSortOrder = itemBefore;
        data[i - 1].tmpSortOrder = (currentItem + itemBefore) / 2;
      } else if (data[i].tmpSortOrder === data[i - 1].tmpSortOrder) {
        // if sortOrder of current record is equal to the sortOrder of previous record ...
        if (data[i + 1] && data[i + 1].tmpSortOrder > data[i - 1].tmpSortOrder) {
          // ... and the current record is between two other records in correct order, use average as sortOrder
          data[i].tmpSortOrder = (data[i + 1].tmpSortOrder + data[i - 1].tmpSortOrder) / 2;
        } else {
          // ... change sortOrder of previous record (use average of the current record and the pre-previous record)
          const currentItem = data[i].tmpSortOrder;
          const itemBeforeBefore = data[i - 2].tmpSortOrder;
          data[i - 1].tmpSortOrder = (currentItem + itemBeforeBefore) / 2;
        }
      }
    }

    return data;
  };

  const startReordering = () => {
    setSortableOrders(calculateTmpSortOrder(filteredData()));
    setIsReordering(true);
  };

  const abortReordering = () => {
    setIsReordering(false);
    setSortableOrders([]);
  };

  const onReordering = ({oldIndex, newIndex}) => {
    if (oldIndex !== newIndex) {
      const data = arrayMove(sortableOrders, oldIndex, newIndex);

      if (data[newIndex - 1] && data[newIndex + 1]) {
        data[newIndex].tmpSortOrder = (data[newIndex - 1].tmpSortOrder + data[newIndex + 1].tmpSortOrder) / 2;
      } else if (data[newIndex + 1]) {
        data[newIndex].tmpSortOrder = data[newIndex + 1].tmpSortOrder - 0.5;
      } else {
        data[newIndex].tmpSortOrder = data[newIndex - 1].tmpSortOrder + 0.5;
      }

      setSortableOrders(data);
    }
  };

  const saveReordering = async () => {
    let skipNotification = false;
    const updates = [];
    // Create a copy of the tmpSortOrders, otherwise, after saving the first change, 'populateAttributesFromStore'
    // can overwrite pending changes with the original 'sortOrder'.
    const newSortOrders = sortableOrders.map((o) => ({id: o.id, tmpSortOrder: o.tmpSortOrder}));
    filteredData().forEach((item) => {
      const sortedItem = newSortOrders.find((i) => i.id === item.id);
      if (item.sortOrder !== sortedItem.tmpSortOrder) {
        item.sortOrder = sortedItem.tmpSortOrder;
        if (item.children) {
          item.children[0].sortOrder = sortedItem.tmpSortOrder;
          updates.push(store.operationStore.reorder({
            id: item.children[0].id,
            sortOrder: item.children[0].sortOrder,
          }, {skipNotification}));
        } else {
          updates.push(store.operationStore.reorder({
            id: item.id,
            sortOrder: item.sortOrder,
          }, {skipNotification}));
        }
        skipNotification = true;
      }
    });
    await Promise.all(updates);
    setIsReordering(false);
  };

  const dataSource = filteredData();

  return (
    <>
      {showTypeSelect && (
        <Col style={{paddingBottom: '1em'}}>
          <Row>
            <Radio.Group
              options={typeOptions}
              onChange={(e) => {
                setLoadType(e.target.value);
                setSelectedOperation(undefined);
                onTypeChange(e.target.value);
              }}
              value={loadType}
              optionType="button"
              disabled={isReordering}
            />
            {reorderPolicy.canExecute() && loadType === Operation.displayStates.planned && (
              <div style={{position: 'absolute', right: 0}}>
                {!isReordering && (
                  <ReorderButton disabled={dataSource.length < 2} onClick={startReordering}/>
                )}
                {isReordering && (
                  <>
                    <CancelButton
                      type={'secondary'}
                      style={{marginRight: '10px'}}
                      onClick={abortReordering}
                    />
                    <SaveButton
                      type={'primary'}
                      onClick={saveReordering}
                    />
                  </>
                )}
              </div>
            )}
          </Row>
        </Col>
      )}
      {!isReordering && (
        <Table
          className={'order-table'}
          rowKey="id"
          columns={tableColumns}
          expandable={{
            defaultExpandAllRows: true,
            expandRowByClick: true,
          }}
          pagination={false}
          rowClassName={rowClassNames}
          onRow={(record) => ({onClick: () => onClick(record)})}
          dataSource={dataSource}
          loading={loading}
          {...props}
        />
      )}
      {isReordering && (
        <SortableTable
          className={'order-table'}
          rowKey="id"
          columns={tableColumns}
          pagination={false}
          rowClassName={rowClassNames}
          dataSource={sortableOrders}
          showDragHandle={(record) => record !== undefined && record.isPlanned}
          dragHandlePosition="last"
          onOrderUpdated={onReordering}
          {...props}
        />
      )}
    </>
  );
};

export default observer(OrderTable);
